import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy
} from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { isAfter } from 'date-fns';
import { Subscription } from 'rxjs';

import { ReactiveComponent } from 'src/app/components/shared/reactive-component';
import {
  BarcodeReaderEventType,
  BarcodeResult,
  GiftCard
} from 'src/app/models';
import { BarcodeProvider } from 'src/app/providers/barcode.provider';
import { LogRocketProvider } from 'src/app/providers/logrocket.provider';
import {
  GetGiftCard,
  GetGiftCardSuccess
} from 'src/app/store/payment/payment.action';

enum GiftCardPaymentModalView {
  SCAN = 'scan',
  MANUAL = 'manual',
  SECURE = 'secure',
  SUMMARY = 'summary' //For TOOLS Gift Card check only
}

export interface IGiftCardPaymentModal {
  isPayment?: boolean;
}

@Component({
  selector: 'pos-gift-card-payment',
  templateUrl: './gift-card.modal.html',
  styleUrls: ['./gift-card.modal.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GiftCardPaymentModal
  extends ReactiveComponent
  implements OnDestroy
{
  @Input() isPayment = true;

  identification_number: string;
  secure_code: number;
  view = GiftCardPaymentModalView.SCAN;
  errorMessage: string;
  giftCard: GiftCard;
  isValidGiftCard = false;
  barcodeSubscription: Subscription;
  readonly GiftCardPaymentModalView = GiftCardPaymentModalView;

  constructor(
    private readonly modalController: ModalController,
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly barcodeProvider: BarcodeProvider,
    private readonly logRocketProvider: LogRocketProvider,
    private readonly cdr: ChangeDetectorRef
  ) {
    super();
  }

  *onSubscribe() {
    this.barcodeSubscription = this.barcodeProvider.subscribe(
      BarcodeReaderEventType.GIFT_CARD,
      (barcode: BarcodeResult) => {
        this.logRocketProvider.log(
          '[Gift Card Check/Payment] BarcodeReaderEventType.GIFT_CARD barcode',
          JSON.stringify(barcode)
        );
        if (this.view === GiftCardPaymentModalView.SCAN) {
          this.identification_number = barcode.barcode;
          this.store.dispatch(new GetGiftCard(this.identification_number));
          this.cdr.markForCheck();
        }
      }
    );

    yield this.actions$
      .pipe(ofActionSuccessful(GetGiftCardSuccess))
      .subscribe(async ({ giftCard }) => {
        this.giftCard = giftCard && giftCard.id ? giftCard : null;
        // - If gift card exists and has balance ( > 0, no need to cover full outstanding), continue to summary OR dismiss. Show error if no balance
        if (!!this.giftCard?.secure_code && this.isPayment) {
          this.view = GiftCardPaymentModalView.SECURE;
          this.cdr.markForCheck();
          return;
        }

        this.view = GiftCardPaymentModalView.SUMMARY;
        this.checkGiftCardValid();
        this.cdr.markForCheck();
      });
  }

  onBack() {
    this.view = GiftCardPaymentModalView.SCAN;
  }

  onManualEntry() {
    this.view = GiftCardPaymentModalView.MANUAL;
  }

  onConfirmManualEntry() {
    if (!this.identification_number) {
      return;
    }

    this.store.dispatch(new GetGiftCard(this.identification_number));
  }

  private checkGiftCardValid() {
    if (!this.giftCard) {
      this.isValidGiftCard = false;
    } else {
      this.isValidGiftCard =
        (!this.giftCard?.expiry_date ||
          isAfter(new Date(this.giftCard?.expiry_date), new Date())) &&
        this.giftCard?.balance > 0;
    }
  }

  onConfirmSecureCode() {
    if (!this.secure_code) {
      return;
    }

    this.errorMessage = null;
    if (this.giftCard.secure_code !== this.secure_code.toString()) {
      this.errorMessage = 'Secure Code incorrect. Please try again.';
      this.secure_code = null;
      return;
    }

    this.view = GiftCardPaymentModalView.SUMMARY;
    this.checkGiftCardValid();
  }

  onConfirmGiftCard() {
    if (!this.giftCard || !this.isValidGiftCard) {
      return;
    }

    this.onCloseModal(this.giftCard);
  }

  onCloseModal(giftCard?: GiftCard) {
    this.modalController.dismiss(giftCard);
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.barcodeProvider.unsubscribe(this.barcodeSubscription);
  }
}
